import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Card, CardContent } from "../../components/ui/card";
import { HeroKPICard } from "./HeroKPICard";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent } from "../../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Separator } from "../../components/ui/separator";
import { Switch } from "../../components/ui/switch";
import { Avatar, AvatarImage } from "../../components/ui/avatar";
import { useToast } from "../../hooks/use-toast";
import apiService from "../../services/apiService";
import LoadingSpinner from "../../components/LoadingSpinner";
import { parseICICIStatement } from "../../lib/bank-statement-parser";
import { runAutoMatch } from "../../lib/reconciliation-engine";
import { calculatePaymentSplit } from "../../lib/payment-utils";
import { BankTransaction, TransactionFlag } from "../../types/reconciliation";
import {
  Invoice,
  InvoiceStatus,
  PaymentRecord,
  getInvoiceStatusColor,
} from "../../types/invoice";
import { format, subMonths } from "date-fns";
import {
  Upload, FileSpreadsheet, CheckCircle2, Clock, AlertCircle, Eye,
  IndianRupee, CreditCard, Check, X, Landmark,
  Calendar, DollarSign, ArrowUpRight, ExternalLink, AlertTriangle, Info,
  ChevronLeft, ChevronRight, Sparkles, FileUp, ShieldCheck, User, Download,
  ArrowLeft
} from "lucide-react";
import { useNavigate } from "react-router-dom";

const getMonthOptions = () => {
  const months = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = subMonths(now, i);
    months.push({ value: format(d, "yyyy-MM"), label: format(d, "MMMM yyyy") });
  }
  return months;
};

const fmt = (n: number) =>
  `₹${n ? n.toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : 0 }`;

function InlineLoader({ label }: { label?: string }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10">
      <div className="h-9 w-9 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
      {label ? <p className="text-sm text-muted-foreground">{label}</p> : null}
    </div>
  );
}

const toAbsoluteLenderAvatarUrl = (avatarPath?: string): string | undefined => {
  if (!avatarPath) return undefined;
  if (/^https?:\/\//i.test(avatarPath)) return avatarPath;
  const baseUrl = String(import.meta.env.VITE_API_BASE_URL || "").replace(/\/+$/, "");
  const cleanPath = String(avatarPath).replace(/^\/+/, "");
  if (!baseUrl) return `/${cleanPath}`;
  return `${baseUrl}/${cleanPath}`;
};

const toAbsoluteFileUrl = toAbsoluteLenderAvatarUrl;

const formatExportDate = (value?: string): string => {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : format(parsed, "dd MMM yyyy");
};

const getPaymentUtr = (inv: ReconciliationInvoice, side: "astrafin" | "lender"): string => {
  const payer = side === "lender" ? "payToLender" : "payToAstrafin";
  const refs = (inv.payments ?? [])
    .filter((p) => p.payer === payer)
    .map((p) => p.reference)
    .filter((ref): ref is string => Boolean(ref && String(ref).trim()));
  return [...new Set(refs)].join(", ");
};

const getExportStatus = (inv: ReconciliationInvoice, side: "astrafin" | "lender"): string => {
  const displayAmt = side === "lender" ? getLenderPayableAmount(inv) : getAstrafinPayableAmount(inv);
  const paid = getSidePaidAmount(inv, side);
  return getSideInvoiceStatus(inv, side, displayAmt, paid);
};

const EXCEL_STATUS_STYLES: Record<string, { fill: string; font: string }> = {
  Paid: { fill: "FFDCFCE7", font: "FF166534" },
  Overdue: { fill: "FFFEE2E2", font: "FF991B1B" },
};

const exportReconciliationInvoices = async (
  invoices: ReconciliationInvoice[],
  side: "astrafin" | "lender",
  month: string
) => {
  const [{ Workbook }, { saveAs }] = await Promise.all([
    import("exceljs"),
    import("file-saver"),
  ]);

  const headers =
    side === "lender"
      ? [
          "Customer Name",
          "Lender Name",
          "RS No.",
          "Instalment No.",
          "Invoice Date",
          "Due Date",
          "Lender Amount",
          "Status",
          "UTR No.",
        ]
      : [
          "Customer Name",
          "RS No.",
          "Instalment No.",
          "Invoice Date",
          "Due Date",
          "Astrafin Receivable",
          "Status",
          "UTR No.",
        ];

  const statusCol = side === "lender" ? 8 : 7;
  const workbook = new Workbook();
  const worksheet = workbook.addWorksheet(side === "lender" ? "Lender" : "Astrafin");

  const headerRow = worksheet.addRow(headers);
  headerRow.font = { bold: true };

  for (const inv of invoices) {
    const status = getExportStatus(inv, side);
    const row = worksheet.addRow([
      inv.customer?.name ?? "",
      ...(side === "lender" ? [inv.lenderName ?? ""] : []),
      inv.rsInvoiceCode ?? "",
      inv.periodNo ?? "",
      formatExportDate(inv.invoiceDate),
      formatExportDate(inv.dueDate),
      side === "lender" ? getLenderPayableAmount(inv) : getAstrafinPayableAmount(inv),
      status,
      getPaymentUtr(inv, side),
    ]);

    const statusStyle = EXCEL_STATUS_STYLES[status];
    if (statusStyle) {
      const cell = row.getCell(statusCol);
      cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: statusStyle.fill } };
      cell.font = { color: { argb: statusStyle.font }, bold: true };
      cell.alignment = { horizontal: "center", vertical: "middle" };
    }
  }

  worksheet.columns.forEach((col) => {
    if (col) col.width = 18;
  });

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    `payment-reconciliation-${side}-${month}.xlsx`
  );
};

const FLAG_CONFIG: Record<TransactionFlag, { label: string; color: string; icon: typeof AlertTriangle }> = {
  duplicate: { label: "Possible Duplicate", color: "text-destructive bg-destructive/10", icon: AlertCircle },
  tds_mismatch: { label: "TDS Mismatch", color: "text-amber-700 bg-amber-50", icon: AlertTriangle },
  overpayment: { label: "Overpayment", color: "text-destructive bg-destructive/10", icon: AlertCircle },
  no_tds_deducted: { label: "No TDS Deducted", color: "text-amber-700 bg-amber-50", icon: AlertTriangle },
  partial: { label: "Partial Payment", color: "text-blue-700 bg-blue-50", icon: Info },
  partial_tranche: { label: "Split Tranche", color: "text-violet-700 bg-violet-50", icon: Info },
};

interface LenderRemittance {
  id: string;
  lenderName?: string;
  lenderAvatar?: string;
  status?: string;
  remittanceDate?: string;
  utr?: string;
  amountRemitted?: number;
}

type ReconciliationInvoice = Invoice & {
  amount?: number;
  gstRaw?: number;
  autoGeneratedPdfLink?: string;
  amountPayableToAstrafin?: number;
  amountPayableToLender?: number;
  amountPaidByAstrafin?: number;
  amountPaidByLender?: number;
  isAstrafinPaid?: boolean;
  isLenderPaid?: boolean;
  astrafinPaidSource?: string | null;
  lenderPaidSource?: string | null;
};

const PAID_SOURCE_EXCEL = "ASTRAFIN_EXCEL";

const pickNumber = (raw: Record<string, unknown>, ...keys: string[]): number => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "" && !Number.isNaN(Number(value))) {
      return Number(value);
    }
  }
  return 0;
};

const pickString = (raw: Record<string, unknown>, ...keys: string[]): string | undefined => {
  for (const key of keys) {
    const value = raw[key];
    if (typeof value === "string" && value.trim() !== "") return value.trim();
  }
  return undefined;
};

interface MonthCardsData {
  totalInvoiceCount: number;
  totalAmount: number;
  paidCount: number;
  paidAmount: number;
  overdueCount: number;
  overdueAmount: number;
}

interface MonthCardsResponse {
  astrafin: MonthCardsData;
  lender: MonthCardsData;
  paidByAstrafin: MonthCardsData;
}

const EMPTY_MONTH_CARDS: MonthCardsData = {
  totalInvoiceCount: 0,
  totalAmount: 0,
  paidCount: 0,
  paidAmount: 0,
  overdueCount: 0,
  overdueAmount: 0,
};

const EMPTY_MONTH_CARDS_RESPONSE: MonthCardsResponse = {
  astrafin: EMPTY_MONTH_CARDS,
  lender: EMPTY_MONTH_CARDS,
  paidByAstrafin: EMPTY_MONTH_CARDS,
};

const normalizeMonthCards = (raw: Record<string, unknown>): MonthCardsData => ({
  totalInvoiceCount: pickNumber(
    raw,
    "totalInvoices",
    "total_invoices",
    "totalInvoiceCount",
    "total_invoice_count"
  ),
  totalAmount: pickNumber(raw, "totalAmount", "total_amount"),
  paidCount: pickNumber(raw, "totalPaidCount", "total_paid_count"),
  paidAmount: pickNumber(raw, "totalPaidAmount", "total_paid_amount"),
  overdueCount: pickNumber(raw, "overdueCount", "overdue_count"),
  overdueAmount: pickNumber(raw, "overdueAmount", "overdue_amount"),
});

const parseMonthCardsResponse = (response: unknown): MonthCardsResponse => {
  const payload = extractMonthCardsPayload(response);
  const section = (key: string, altKey?: string) =>
    normalizeMonthCards(
      ((payload[key] ?? (altKey ? payload[altKey] : undefined)) ?? {}) as Record<string, unknown>
    );

  return {
    astrafin: section("astrafin"),
    lender: section("lender"),
    paidByAstrafin: section("paidByAstrafin", "paid_by_astrafin"),
  };
};

const extractMonthCardsPayload = (response: unknown): Record<string, unknown> => {
  if (!response || typeof response !== "object") return {};
  const root = response as Record<string, unknown>;
  const layer1 = root.data ?? root;
  if (!layer1 || typeof layer1 !== "object") return {};
  const layer2 = layer1 as Record<string, unknown>;
  const layer3 = layer2.data ?? layer2.cards ?? layer2;
  return (layer3 && typeof layer3 === "object" ? layer3 : {}) as Record<string, unknown>;
};

const getAstrafinPayableAmount = (inv: ReconciliationInvoice): number =>
  Math.max(0, inv.amountPayableToAstrafin ?? 0);

const getLenderPayableAmount = (inv: ReconciliationInvoice): number =>
  Math.max(0, inv.amountPayableToLender ?? 0);

const sumPaymentsByPayer = (
  inv: ReconciliationInvoice,
  payer: NonNullable<PaymentRecord["payer"]>
): number =>
  (inv.payments ?? [])
    .filter((p) => p.payer === payer)
    .reduce((sum, p) => sum + (typeof p.amount === "number" ? p.amount : 0), 0);

const hasPaymentForPayer = (
  inv: ReconciliationInvoice,
  payer: NonNullable<PaymentRecord["payer"]>
): boolean => (inv.payments ?? []).some((p) => p.payer === payer);

const getAstrafinPaidAmount = (inv: ReconciliationInvoice): number => {
  const payable = getAstrafinPayableAmount(inv);
  if (inv.isAstrafinPaid && payable > 0) return payable;
  const fromPayments = sumPaymentsByPayer(inv, "payToAstrafin");
  if (fromPayments > 0) return fromPayments;
  return Math.max(0, inv.amountPaidByAstrafin ?? 0);
};

const getLenderPaidAmount = (inv: ReconciliationInvoice): number => {
  const payable = getLenderPayableAmount(inv);
  if (inv.isLenderPaid && payable > 0) return payable;
  const fromPayments = sumPaymentsByPayer(inv, "payToLender");
  if (fromPayments > 0) return fromPayments;
  return Math.max(0, inv.amountPaidByLender ?? 0);
};

const getSidePaidAmount = (inv: ReconciliationInvoice, side: "astrafin" | "lender"): number =>
  side === "astrafin" ? getAstrafinPaidAmount(inv) : getLenderPaidAmount(inv);

const getSideOutstandingAmount = (inv: ReconciliationInvoice, side: "astrafin" | "lender"): number => {
  const payable = side === "astrafin" ? getAstrafinPayableAmount(inv) : getLenderPayableAmount(inv);
  const paid = getSidePaidAmount(inv, side);
  return Math.max(0, Math.round(payable - paid));
};

const hasExcelReconciliation = (
  inv: ReconciliationInvoice,
  side: "astrafin" | "lender"
): boolean =>
  side === "astrafin"
    ? inv.astrafinPaidSource === PAID_SOURCE_EXCEL
    : inv.lenderPaidSource === PAID_SOURCE_EXCEL;

const isAstrafinTabInvoice = (inv: ReconciliationInvoice): boolean =>
  getAstrafinPayableAmount(inv) > 0;

const isLenderTabInvoice = (inv: ReconciliationInvoice): boolean =>
  getLenderPayableAmount(inv) > 0;

const getSideInvoiceStatus = (
  inv: ReconciliationInvoice,
  side: "astrafin" | "lender",
  displayAmt: number,
  paid: number
): InvoiceStatus => {
  const isOverdue = inv.dueDate ? new Date(inv.dueDate) < new Date() : false;
  const payer = side === "lender" ? "payToLender" : "payToAstrafin";
  const isSidePaid =
    side === "lender"
      ? inv.isLenderPaid
      : inv.isAstrafinPaid;

  if (isSidePaid || hasPaymentForPayer(inv, payer) || (displayAmt > 0 && paid >= displayAmt)) {
    return "Paid";
  }
  return isOverdue ? "Overdue" : "Pending";
};

const getOutstandingAmount = (inv: ReconciliationInvoice): number =>
  getSideOutstandingAmount(inv, "astrafin");

const normalizeInvoice = (raw: Record<string, unknown>): ReconciliationInvoice => {
  const payments = Array.isArray(raw.payments) ? (raw.payments as PaymentRecord[]) : [];
  const paid =
    typeof raw.paid === "number"
      ? raw.paid
      : payments.reduce((sum, p) => sum + (p.amount ?? 0), 0);
  const amount =
    typeof raw.amount === "number"
      ? raw.amount
      : typeof raw.grandTotalRounded === "number"
        ? raw.grandTotalRounded
        : 0;
  return {
    ...(raw as unknown as Invoice),
    payments,
    paid,
    amount,
    gstRaw: pickNumber(raw, "gstRaw", "gst_raw"),
    autoGeneratedPdfLink: pickString(
      raw,
      "autoGeneratedPdfLink",
      "auto_generated_pdf_link"
    ),
    amountPayableToAstrafin: pickNumber(
      raw,
      "amount_payable_to_astrafin",
      "amountPayableToAstrafin"
    ),
    amountPayableToLender: pickNumber(
      raw,
      "amount_payable_to_lender",
      "amountPayableToLender"
    ),
    amountPaidByAstrafin: pickNumber(
      raw,
      "amount_paid_by_astrafin",
      "amountPaidByAstrafin"
    ),
    amountPaidByLender: pickNumber(
      raw,
      "amount_paid_by_lender",
      "amountPaidByLender",
      "paid_to_lender",
      "paidToLender"
    ),
    customerId: String(raw.customerId ?? (raw.customer as { customerId?: string })?.customerId ?? ""),
    customer: {
      ...((raw.customer as Invoice["customer"]) ?? {}),
      customerId: String(
        raw.customerId ?? (raw.customer as { customerId?: string })?.customerId ?? ""
      ),
      name: String(
        raw.customerName ??
          (raw.customer as { name?: string })?.name ??
          ""
      ),
    },
    isAstrafinPaid: Boolean(raw.isAstrafinPaid),
    isLenderPaid: Boolean(raw.isLenderPaid),
    astrafinPaidSource:
      raw.astrafinPaidSource != null ? String(raw.astrafinPaidSource) : null,
    lenderPaidSource:
      raw.lenderPaidSource != null ? String(raw.lenderPaidSource) : null,
    grandTotalRounded: amount,
  } as ReconciliationInvoice;
};

const PaymentReconciliation = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState<ReconciliationInvoice[]>([]);
  const invoicesRef = useRef(invoices);
  invoicesRef.current = invoices;
  const [lenderRemittances, setLenderRemittances] = useState<LenderRemittance[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthCardsByTab, setMonthCardsByTab] = useState<MonthCardsResponse>(EMPTY_MONTH_CARDS_RESPONSE);
  const [monthCardsLoading, setMonthCardsLoading] = useState(false);

  const monthOptions = useMemo(() => getMonthOptions(), []);
  const [selectedMonth, setSelectedMonth] = useState(monthOptions[0].value);
  const [activeTab, setActiveTab] = useState("astrafin");

  const activeMonthCards = useMemo((): MonthCardsData => {
    if (activeTab === "lender") return monthCardsByTab.lender;
    if (activeTab === "remit") return monthCardsByTab.paidByAstrafin;
    return monthCardsByTab.astrafin;
  }, [activeTab, monthCardsByTab]);

  const loadMonthCards = useCallback(async () => {
    const params = new URLSearchParams({ month: selectedMonth });

    setMonthCardsLoading(true);
    try {
      const response = await apiService.get<unknown>(
        `/rental-schedule/get-rental-schedule-invoice-month-cards?${params.toString()}`
      );
      setMonthCardsByTab(parseMonthCardsResponse(response));
    } catch (error) {
      console.error("Failed to load month cards:", error);
      setMonthCardsByTab(EMPTY_MONTH_CARDS_RESPONSE);
    } finally {
      setMonthCardsLoading(false);
    }
  }, [selectedMonth]);

  const loadInvoices = useCallback(async () => {
    const params = new URLSearchParams({
      page: "1",
      pageSize: "500",
      tab: "due",
      month: selectedMonth,
    });

    const invoiceResponse = await apiService.get<{ data?: unknown[] }>(
      `/rental-schedule/get-rental-schedule-invoices?${params.toString()}`
    );

    const invoiceRows = Array.isArray(invoiceResponse?.data) ? invoiceResponse.data : [];
    setInvoices(invoiceRows.map((row) => normalizeInvoice(row as Record<string, unknown>)));
  }, [selectedMonth]);

  const loadPageData = useCallback(async () => {
    setLoading(true);
    try {
      await loadInvoices();
      await loadMonthCards();
    } catch (error) {
      console.error("Failed to load reconciliation data:", error);
      toast({
        title: "Failed to load data",
        description: "Could not fetch due invoices for this month.",
        variant: "destructive",
      });
      setInvoices([]);
      setMonthCardsByTab(EMPTY_MONTH_CARDS_RESPONSE);
    } finally {
      setLoading(false);
    }
  }, [loadInvoices, loadMonthCards, toast]);

  const refreshData = useCallback(async () => {
    await loadInvoices();
    await loadMonthCards();
  }, [loadInvoices, loadMonthCards]);

  useEffect(() => {
    void loadPageData();
  }, [loadPageData]);

  // Bulk reconciliation
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTransactions, setBulkTransactions] = useState<BankTransaction[]>([]);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkFileName, setBulkFileName] = useState("");
  const [showMatchesOnly, setShowMatchesOnly] = useState(true);

  const bulkIgnoreCloseRef = useRef(false);

  const resetBulkDialogState = useCallback(() => {
    setBulkTransactions([]);
    setBulkFileName("");
    setBulkProcessing(false);
    setBulkApplying(false);
    setShowMatchesOnly(true);
    const input = document.getElementById("bulk-file-input") as HTMLInputElement | null;
    if (input) input.value = "";
  }, []);

  const openBulkDialog = useCallback(() => {
    resetBulkDialogState();
    bulkIgnoreCloseRef.current = true;
    setBulkOpen(true);
    window.setTimeout(() => {
      bulkIgnoreCloseRef.current = false;
    }, 200);
  }, [resetBulkDialogState]);

  const closeBulkDialog = useCallback(() => {
    if (bulkApplying || bulkProcessing) return;
    resetBulkDialogState();
    setBulkOpen(false);
  }, [bulkApplying, bulkProcessing, resetBulkDialogState]);

  const handleBulkOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setBulkOpen(true);
        return;
      }
      if (bulkIgnoreCloseRef.current || bulkApplying || bulkProcessing) return;
      resetBulkDialogState();
      setBulkOpen(false);
    },
    [bulkApplying, bulkProcessing, resetBulkDialogState]
  );

  const recordPayment = useCallback(
    async (
      invoiceId: string,
      payment: {
        amount: number;
        date: string;
        payer?: string;
        paymentMode?: string;
        reference?: string;
        notes?: string;
        paidSource?: 'manual' | 'ASTRAFIN_EXCEL';
      },
      options?: { refresh?: boolean; capAmount?: boolean }
    ) => {
      const inv = invoicesRef.current.find((i) => i.invoiceId === invoiceId);
      const outstanding = inv
        ? payment.payer === "payToLender"
          ? getSideOutstandingAmount(inv, "lender")
          : getOutstandingAmount(inv)
        : 0;
      const received = Math.round(payment.amount);
      const paidSource = payment.paidSource ?? 'ASTRAFIN_EXCEL';
      const shouldCap =
        options?.capAmount ?? paidSource !== 'ASTRAFIN_EXCEL';
      const effectiveAmount = shouldCap
        ? outstanding > 0
          ? Math.min(received, outstanding)
          : received
        : received;

      if (effectiveAmount <= 0) return;

      const notes =
        payment.notes ??
        `Payment via ${payment.paymentMode ?? "NEFT"}${
          payment.reference ? ` - Ref: ${payment.reference}` : ""
        }`;

      await apiService.post<{ data?: PaymentRecord; message?: string }>(
        `/rental-schedule/create-rental-schedule-invoice-payment`,
        {
          invoiceId,
          amount: effectiveAmount,
          date: payment.date,
          paymentMode: payment.paymentMode,
          reference: payment.reference,
          payer: payment.payer ?? "payToAstrafin",
          notes,
          paidSource,
        }
      );

      if (options?.refresh !== false) {
        await refreshData();
      }
    },
    [refreshData]
  );

  const markLenderRemitted = useCallback(
    (remitId: string, details: {
      remittanceDate: string;
      paymentMode: string;
      utr: string;
      notes?: string;
      amountRemitted?: number;
    }) => {
      setLenderRemittances((prev) =>
        prev.map((lr) =>
          lr.id === remitId
            ? {
                ...lr,
                status: "Remitted",
                remittanceDate: details.remittanceDate,
                utr: details.utr,
                amountRemitted: details.amountRemitted,
              }
            : lr
        )
      );
    },
    []
  );

  // Lender Remittance modal state
  const [remitOpen, setRemitOpen] = useState(false);
  const [remitId, setRemitId] = useState<string | null>(null);
  const [remitDate, setRemitDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [remitMode, setRemitMode] = useState("NEFT");
  const [remitUtr, setRemitUtr] = useState("");
  const [remitAmount, setRemitAmount] = useState("");
  const [remitNotes, setRemitNotes] = useState("");

  const [selectedInvoice, setSelectedInvoice] = useState<ReconciliationInvoice | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // (Reconcile-from-statement is bulk-only; the side-sheet only marks payments manually.)

  // Manual payment form
  const [payAmount, setPayAmount] = useState("");
  const [payDate, setPayDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [payMode, setPayMode] = useState("NEFT");
  const [payRef, setPayRef] = useState("");
  const [isRecordingPayment, setIsRecordingPayment] = useState(false);

  // Astrafin tab: invoices where Astrafin receives money
  const astrafInvoices = useMemo(
    (): ReconciliationInvoice[] => invoices.filter(isAstrafinTabInvoice),
    [invoices]
  );

  // Lender tab: invoices with lender payable amount
  const lenderInvoices = useMemo(
    (): ReconciliationInvoice[] => invoices.filter(isLenderTabInvoice),
    [invoices]
  );

  const lenderUnpaidSummary = useMemo(() => {
    const unpaid = lenderInvoices.filter((inv) => {
      const displayAmt = getLenderPayableAmount(inv);
      const paid = getSidePaidAmount(inv, "lender");
      return getSideInvoiceStatus(inv, "lender", displayAmt, paid) !== "Paid";
    });
    return {
      count: unpaid.length,
      amount: unpaid.reduce((sum, inv) => sum + getSideOutstandingAmount(inv, "lender"), 0),
    };
  }, [lenderInvoices]);

  const [markAllLenderPaidOpen, setMarkAllLenderPaidOpen] = useState(false);
  const [markAllLenderPaidLoading, setMarkAllLenderPaidLoading] = useState(false);

  const handleExportExcel = useCallback(
    async (side: "astrafin" | "lender") => {
      const list = side === "lender" ? lenderInvoices : astrafInvoices;
      if (list.length === 0) {
        toast({
          title: "Nothing to export",
          description: "No invoices available for this tab and month.",
          variant: "destructive",
        });
        return;
      }
      try {
        await exportReconciliationInvoices(list, side, selectedMonth);
        toast({
          title: "Export started",
          description: `Downloaded ${list.length} ${side === "lender" ? "lender" : "Astrafin"} invoice(s).`,
        });
      } catch (error) {
        console.error("Failed to export reconciliation invoices:", error);
        toast({
          title: "Export failed",
          description: "Could not generate the Excel file. Please try again.",
          variant: "destructive",
        });
      }
    },
    [astrafInvoices, lenderInvoices, selectedMonth, toast]
  );

  const handleMarkAllLenderPaid = useCallback(async () => {
    setMarkAllLenderPaidLoading(true);
    try {
      await apiService.post<{ message?: string; data?: unknown }>(
        `/rental-schedule/mark-all-rental-schedule-invoices-lender-paid`,
        {
          month: selectedMonth,
          payer: "payToLender",
          paidSource: "manual",
        }
      );
      toast({
        title: "Lender payments recorded",
        description: `Marked ${lenderUnpaidSummary.count} invoice(s) as paid to lender for ${selectedMonth}.`,
      });
      setMarkAllLenderPaidOpen(false);
      await refreshData();
    } catch (error) {
      console.error("Failed to mark all lender invoices as paid:", error);
      toast({
        title: "Mark paid failed",
        description: "Could not mark all lender invoices as paid. Please try again.",
        variant: "destructive",
      });
    } finally {
      setMarkAllLenderPaidLoading(false);
    }
  }, [lenderUnpaidSummary.count, refreshData, selectedMonth, toast]);

  const getScheduleDisplayIds = (inv: Invoice) => {
    if (inv.rentalScheduleDisplayId) {
      return `RS-${String(inv.rentalScheduleDisplayId).padStart(2, "0")}`;
    }
    return inv.scheduleId ? String(inv.scheduleId) : "—";
  };

  const getInstalmentNo = (inv: Invoice): string => inv.periodNo || "—";

  const isInvoiceLocked = (inv: ReconciliationInvoice) => {
    const side = activeTab === "lender" ? "lender" : "astrafin";
    const status = getSideInvoiceStatus(
      inv,
      side,
      side === "astrafin" ? getAstrafinPayableAmount(inv) : getLenderPayableAmount(inv),
      getSidePaidAmount(inv, side)
    );
    return status === "Paid";
  };

  // Mark paid handler
  const handleMarkPaid = async () => {
    if (!selectedInvoice?.invoiceId || !payAmount || parseFloat(payAmount) <= 0) {
      toast({ title: "Invalid input", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    if (isInvoiceLocked(selectedInvoice)) {
      toast({ title: "Invoice locked", description: "This invoice is already fully paid", variant: "destructive" });
      return;
    }
    if (isRecordingPayment) return;

    setIsRecordingPayment(true);
    try {
      const payer = activeTab === "lender" ? "payToLender" : "payToAstrafin";
      await recordPayment(selectedInvoice.invoiceId, {
        amount: parseFloat(payAmount),
        date: payDate,
        payer,
        paymentMode: payMode,
        reference: payRef.trim() || undefined,
        paidSource: 'manual',
      });
      toast({ title: "Payment recorded", description: `${fmt(parseFloat(payAmount))} marked for ${selectedInvoice.invoiceId}` });
      resetForm();
      setSelectedInvoice(null);
      setSheetOpen(false);
    } catch (error) {
      console.error("Failed to record payment:", error);
      toast({ title: "Payment failed", description: "Could not save payment. Please try again.", variant: "destructive" });
    } finally {
      setIsRecordingPayment(false);
    }
  };

  const resetForm = () => {
    setPayAmount("");
    setPayDate(format(new Date(), "yyyy-MM-dd"));
    setPayMode("NEFT");
    setPayRef("");
  };

  const openSheet = (inv: ReconciliationInvoice) => {
    const freshInv = invoices.find((i) => i.invoiceId === inv.invoiceId) || inv;
    setSelectedInvoice(freshInv);
    resetForm();
    setPayAmount(String(getSideOutstandingAmount(freshInv, activeTab === "lender" ? "lender" : "astrafin")));
    setSheetOpen(true);
  };

  // File upload handlers
  const handleBulkFile = useCallback(async (file: File) => {
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      toast({ title: "Invalid file", description: "Please upload an ICICI Excel (.xlsx) file", variant: "destructive" });
      return;
    }
    setBulkProcessing(true);
    setBulkFileName(file.name);
    try {
      const buffer = await file.arrayBuffer();
      const parsed = parseICICIStatement(buffer);
      const matched = runAutoMatch(parsed, invoicesRef.current, { useAstrafInAmount: true });
      setBulkTransactions(matched.filter((t) => t.transactionType === "credit"));
      toast({ title: "Statement parsed", description: `${parsed.length} transactions found` });
    } catch (e) {
      toast({ title: "Parse error", description: String(e), variant: "destructive" });
    } finally {
      setBulkProcessing(false);
    }
  }, [toast]);


  // UTR deduplication check
  const isUtrAlreadyRecorded = useCallback((utr: string | undefined): boolean => {
    if (!utr) return false;
    const upper = utr.toUpperCase();
    return invoices.some((inv:any) => inv.payments.some((p:any) => p.reference?.toUpperCase() === upper));
  }, [invoices]);

  const applyMatch = async (
    txn: BankTransaction,
    invoiceId: string,
    options?: { silent?: boolean }
  ) => {
    const inv = invoicesRef.current.find((i) => i.invoiceId === invoiceId);
    if (inv && isInvoiceLocked(inv)) {
      toast({ title: "Invoice locked", description: "This invoice is already fully paid", variant: "destructive" });
      return false;
    }

    const ref = txn.utrNumber || txn.referenceNumber;
    if (ref && isUtrAlreadyRecorded(ref)) {
      toast({ title: "Duplicate UTR", description: `Reference ${ref} is already recorded against an invoice`, variant: "destructive" });
      return false;
    }

    const matchedIds = txn.matchedInvoiceIds || [];
    if (matchedIds.length > 1 && matchedIds.includes(invoiceId)) {
      const matchedInvs = matchedIds
        .map((id) => invoicesRef.current.find((i) => i.invoiceId === id))
        .filter(Boolean) as ReconciliationInvoice[];
      const ordered = [...matchedInvs].sort(
        (a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
      );

      let remaining = txn.depositAmount;
      let appliedCount = 0;
      for (const matchInv of ordered) {
        if (remaining <= 1) break;
        if (isInvoiceLocked(matchInv)) continue;
        const due = getOutstandingAmount(matchInv);
        if (due <= 0) continue;
        const alloc = Math.min(due, remaining);
        if (!matchInv.invoiceId) continue;
        await recordPayment(matchInv.invoiceId, {
          amount: Math.round(alloc),
          date: txn.valueDate,
          payer: "payToAstrafin",
          paymentMode: txn.paymentMode || "OTHER",
          reference: ref || undefined,
          paidSource: "ASTRAFIN_EXCEL",
        }, { refresh: false });
        remaining -= alloc;
        appliedCount += 1;
      }

      const residue = Math.round(remaining);
      if (!options?.silent) {
        toast({
          title: "Clubbed payment allocated (FIFO)",
          description: residue > 0
            ? `${fmt(txn.depositAmount)} → ${appliedCount} invoice(s); ${fmt(residue)} left as customer advance`
            : `${fmt(txn.depositAmount)} split across ${appliedCount} invoice(s)`,
        });
      }
      setBulkTransactions((prev) => prev.filter((t) => t.id !== txn.id));
      return appliedCount > 0;
    }

    const payAmount = Math.round(txn.depositAmount);

    await recordPayment(invoiceId, {
      amount: payAmount,
      date: txn.valueDate,
      payer: "payToAstrafin",
      paymentMode: txn.paymentMode || "OTHER",
      reference: ref || undefined,
      paidSource: "ASTRAFIN_EXCEL",
    }, { refresh: false });
    if (!options?.silent) {
      toast({ title: "Payment applied", description: `${fmt(payAmount)} matched to ${invoiceId}` });
    }
    setBulkTransactions((prev) => prev.filter((t) => t.id !== txn.id));
    return true;
  };

  const acceptMatch = async (txn: BankTransaction, invoiceId: string) => {
    setBulkApplying(true);
    try {
      const applied = await applyMatch(txn, invoiceId);
      if (applied) await refreshData();
    } catch (error) {
      console.error("Failed to apply payment:", error);
      toast({ title: "Payment failed", description: "Could not save matched payment.", variant: "destructive" });
    } finally {
      setBulkApplying(false);
    }
  };

  // One API call per invoice — combine all tranche credits into a single payment.
  const applyTrancheGroup = async (
    txn: BankTransaction,
    options?: { silent?: boolean }
  ): Promise<boolean> => {
    const part = txn.tranchePart;
    if (!part) return false;
    const invoiceId = txn.matchedInvoiceIds?.[0];
    if (!invoiceId) return false;
    const inv = invoicesRef.current.find((i) => i.invoiceId === invoiceId);
    if (!inv) return false;
    if (isInvoiceLocked(inv)) {
      toast({ title: "Invoice locked", description: "This invoice is already fully paid", variant: "destructive" });
      return false;
    }

    const siblings = bulkTransactions.filter((t) => part.siblingTxnIds.includes(t.id));
    if (siblings.length === 0) return false;

    for (const s of siblings) {
      const ref = s.utrNumber || s.referenceNumber;
      if (ref && isUtrAlreadyRecorded(ref)) {
        toast({
          title: "Duplicate UTR",
          description: `Reference ${ref} is already recorded — resolve before accepting tranche`,
          variant: "destructive",
        });
        return false;
      }
    }

    const totalAmount = siblings.reduce((sum, s) => sum + Math.round(s.depositAmount), 0);
    const references = siblings
      .map((s) => s.utrNumber || s.referenceNumber)
      .filter((ref): ref is string => Boolean(ref));
    const paymentDate = siblings.reduce(
      (latest, s) => (new Date(s.valueDate) > new Date(latest) ? s.valueDate : latest),
      siblings[0].valueDate
    );

    await recordPayment(
      invoiceId,
      {
        amount: totalAmount,
        date: paymentDate,
        payer: "payToAstrafin",
        paymentMode: txn.paymentMode || "OTHER",
        reference: references[0],
        notes:
          references.length > 0
            ? `Tranche payment (${siblings.length} credits) — Ref: ${references.join(", ")}`
            : `Tranche payment (${siblings.length} credits)`,
        paidSource: "ASTRAFIN_EXCEL",
      },
      { refresh: false }
    );

    if (!options?.silent) {
      toast({
        title: "Tranche payment applied",
        description: `${siblings.length} linked credits → ${invoiceId} (${fmt(totalAmount)})`,
      });
    }
    setBulkTransactions((prev) => prev.filter((t) => !part.siblingTxnIds.includes(t.id)));
    return true;
  };

  const acceptTrancheGroup = async (txn: BankTransaction) => {
    setBulkApplying(true);
    try {
      const applied = await applyTrancheGroup(txn);
      if (applied) await refreshData();
    } catch (error) {
      console.error("Failed to apply tranche payment:", error);
      toast({ title: "Payment failed", description: "Could not save tranche payment.", variant: "destructive" });
    } finally {
      setBulkApplying(false);
    }
  };

  const acceptBulkHighConfidence = async () => {
    const high = bulkTransactions.filter(t =>
      t.matchConfidence && t.matchConfidence >= 90
      && t.matchedInvoiceIds?.length
      && (!t.flags || t.flags.length === 0)
    );
    if (high.length === 0) {
      toast({ title: "No matches", description: "No high-confidence matches to accept", variant: "destructive" });
      return;
    }
    setBulkApplying(true);
    let count = 0;
    const processedTrancheGroups = new Set<string>();
    try {
      for (const txn of high) {
        if (!txn.matchedInvoiceIds?.length) continue;
        if (txn.tranchePart) {
          const groupId = txn.tranchePart.groupId;
          if (processedTrancheGroups.has(groupId)) continue;
          processedTrancheGroups.add(groupId);
          const applied = await applyTrancheGroup(txn, { silent: true });
          if (applied) count++;
          continue;
        }
        const applied = await applyMatch(txn, txn.matchedInvoiceIds[0], { silent: true });
        if (applied) count++;
      }
      await refreshData();
      toast({ title: "Bulk reconciliation", description: `${count} transactions applied` });
    } catch (error) {
      console.error("Bulk accept failed:", error);
      toast({ title: "Bulk accept failed", description: "Some payments could not be applied.", variant: "destructive" });
    } finally {
      setBulkApplying(false);
    }
  };

  const statusIcon = (status: string) => {
    switch (status) {
      case "Paid": return <CheckCircle2 className="h-3.5 w-3.5" />;
      case "Overdue": return <AlertCircle className="h-3.5 w-3.5" />;
      default: return <Clock className="h-3.5 w-3.5" />;
    }
  };

  const freshSelectedInvoice = selectedInvoice
    ? invoices.find((i) => i.invoiceId === selectedInvoice.invoiceId) || selectedInvoice
    : null;
  const selectedSheetSide: "astrafin" | "lender" = activeTab === "lender" ? "lender" : "astrafin";
  const selectedSheetPayer = selectedSheetSide === "lender" ? "payToLender" : "payToAstrafin";
  const selectedSheetPayments = freshSelectedInvoice
    ? (freshSelectedInvoice.payments ?? []).filter((p) => p.payer === selectedSheetPayer)
    : [];
  const selectedSheetStatus = freshSelectedInvoice
    ? getSideInvoiceStatus(
        freshSelectedInvoice,
        selectedSheetSide,
        selectedSheetSide === "lender"
          ? getLenderPayableAmount(freshSelectedInvoice)
          : getAstrafinPayableAmount(freshSelectedInvoice),
        getSidePaidAmount(freshSelectedInvoice, selectedSheetSide)
      )
    : "Pending";
  const selectedSplit = freshSelectedInvoice ? calculatePaymentSplit(freshSelectedInvoice) : null;

  // Navigation across invoices in the active tab (for prev/next in side sheet)
  const currentTabInvoices = activeTab === "astrafin" ? astrafInvoices : activeTab === "lender" ? lenderInvoices : [];
  const currentIndex = freshSelectedInvoice ? currentTabInvoices.findIndex((i:any) => i.invoiceId === freshSelectedInvoice.invoiceId) : -1;
  const navigateInvoice = (dir: -1 | 1) => {
    if (currentIndex < 0) return;
    const next = currentTabInvoices[currentIndex + dir];
    if (next) openSheet(next);
  };

  // Bulk dialog filtering
  const filteredBulkTxns = useMemo(() => {
    if (!showMatchesOnly) return bulkTransactions;
    return bulkTransactions.filter((t:any) =>
      t.matchStatus === 'suggested' || t.matchStatus === 'matched' || (t.flags && t.flags.length > 0)
    );
  }, [bulkTransactions, showMatchesOnly]);

  const remitPendingCount = useMemo(
    () => lenderRemittances.filter((lr) => lr.status === "Pending").length,
    [lenderRemittances]
  );

  const reconciliationTabItems = useMemo(
    () => [
      { key: "astrafin", label: "Astrafin", icon: <CreditCard className="h-3.5 w-3.5" />, count: astrafInvoices.length },
      { key: "lender", label: "Lender", icon: <Landmark className="h-3.5 w-3.5" />, count: lenderInvoices.length },
      { key: "remit", label: "Lender Remittances", icon: <ArrowUpRight className="h-3.5 w-3.5" />, count: remitPendingCount },
    ],
    [astrafInvoices.length, lenderInvoices.length, remitPendingCount]
  );

  const monthPicker = (
    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
      <SelectTrigger className="w-[180px]">
        <Calendar className="h-4 w-4 mr-2 text-muted-foreground" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {monthOptions.map((m) => (
          <SelectItem key={m.value} value={m.value}>
            {m.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 p-4 bg-white w-full">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">Payment Reconciliation</h1>
        <div className="flex items-center gap-2 flex-wrap justify-end">
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => navigate("/manage-rental-invoice")}
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            onClick={() => {
              if (activeTab !== "astrafin" && activeTab !== "lender") {
                toast({
                  title: "Nothing to export",
                  description: "Switch to Astrafin or Lender tab to export invoices.",
                  variant: "destructive",
                });
                return;
              }
              void handleExportExcel(activeTab);
            }}
          >
            <Download className="h-4 w-4" /> Export
          </Button>
          <Button onClick={openBulkDialog} className="gap-2 shrink-0">
            <Upload className="h-4 w-4" /> Upload Statement
          </Button>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <HeroKPICard
          title="Total Invoices"
          value={monthCardsLoading ? "—" : activeMonthCards.totalInvoiceCount}
          icon={<IndianRupee className="h-4 w-4" />}
          variant="default"
        />
        <HeroKPICard
          title="Total Amount"
          value={monthCardsLoading ? "—" : fmt(activeMonthCards.totalAmount)}
          icon={<DollarSign className="h-4 w-4" />}
          variant="info"
        />
        <HeroKPICard
          title="Paid"
          value={monthCardsLoading ? "—" : activeMonthCards.paidCount}
          subtitle={monthCardsLoading ? undefined : fmt(activeMonthCards.paidAmount)}
          icon={<CheckCircle2 className="h-4 w-4" />}
          variant="success"
        />
        <HeroKPICard
          title="Overdue"
          value={monthCardsLoading ? "—" : activeMonthCards.overdueCount}
          subtitle={monthCardsLoading ? undefined : fmt(activeMonthCards.overdueAmount)}
          icon={<AlertCircle className="h-4 w-4" />}
          variant="danger"
        />
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-1 p-1 bg-muted/50 rounded-lg w-fit">
            {reconciliationTabItems.map((tab) => (
              <Button
                key={tab.key}
                variant={activeTab === tab.key ? "default" : "ghost"}
                size="sm"
                className={`gap-1.5 h-8 text-xs ${activeTab === tab.key ? "" : "text-muted-foreground"}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.icon}
                {tab.label}
                <span
                  className={`ml-0.5 text-[10px] font-bold rounded-full h-4 min-w-[16px] px-1 flex items-center justify-center ${
                    activeTab === tab.key
                      ? "bg-primary-foreground/20 text-primary-foreground"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {tab.count}
                </span>
              </Button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            {monthPicker}
            {activeTab === "lender" && (
              <Button
                size="sm"
                className="gap-2"
                disabled={lenderUnpaidSummary.count === 0 || markAllLenderPaidLoading}
                onClick={() => setMarkAllLenderPaidOpen(true)}
              >
                <CheckCircle2 className="h-4 w-4" /> Mark Paid All
              </Button>
            )}
          </div>
        </div>

        <TabsContent value="astrafin" className="mt-4">
          <InvoiceTable
            invoices={astrafInvoices}
            statusSide="astrafin"
            onRowClick={openSheet}
            getScheduleDisplayIds={getScheduleDisplayIds}
            getInstalmentNo={getInstalmentNo}
            getPaidAmount={(inv) => getSidePaidAmount(inv, "astrafin")}
            statusIcon={statusIcon}
            amountLabel="Astrafin Receivable"
            getDisplayAmount={getAstrafinPayableAmount}
            showLender={false}
          />
        </TabsContent>
        <TabsContent value="lender" className="mt-4">
          <InvoiceTable
            invoices={lenderInvoices}
            statusSide="lender"
            onRowClick={openSheet}
            getScheduleDisplayIds={getScheduleDisplayIds}
            getInstalmentNo={getInstalmentNo}
            getPaidAmount={(inv) => getSidePaidAmount(inv, "lender")}
            statusIcon={statusIcon}
            amountLabel="Lender Amount"
            getDisplayAmount={getLenderPayableAmount}
            showLender
          />
        </TabsContent>
        <TabsContent value="remit" className="mt-4">
          <Card>
            <CardContent className="p-0">
              {lenderRemittances.length === 0 ? (
                <div className="p-10 text-center text-sm text-muted-foreground">
                  No pass-through collections yet. Once a customer pays an invoice routed via Astrafin, the onward remittance to the lender will appear here.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Lender</TableHead>
                      <TableHead>NoA</TableHead>
                      <TableHead className="text-right">Collected on</TableHead>
                      <TableHead className="text-right">Amount Due</TableHead>
                      <TableHead className="text-right">Remitted</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lenderRemittances.map((lr:any) => (
                      <TableRow key={lr.id+Math.random().toString(36).substring(2, 15)}>
                        <TableCell className="font-mono text-xs">{lr.invoiceId}</TableCell>
                        <TableCell className="text-xs">{lr.customerName || '—'}</TableCell>
                        <TableCell className="text-xs">
                          {lr.lenderName || lr.lenderAvatar ? (
                            <div className="flex items-center gap-2 min-w-0">
                              {lr.lenderAvatar ? (
                                <Avatar className="rounded-md shrink-0" style={{ height: "28px", width: "44px" }}>
                                  <AvatarImage
                                    src={toAbsoluteLenderAvatarUrl(lr.lenderAvatar)}
                                    alt={lr.lenderName || "Lender"}
                                    className="object-contain"
                                  />
                                </Avatar>
                              ) : null}
                              <span className="truncate">{lr.lenderName || "—"}</span>
                            </div>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                        <TableCell className="text-xs">
                          {lr.noaId ? (
                            <a href={`/noa/${lr.noaId}`} className="text-primary hover:underline">{lr.noaId}</a>
                          ) : '—'}
                        </TableCell>
                        <TableCell className="text-right text-xs">{lr.collectedAt ? format(new Date(lr.collectedAt), 'dd MMM yyyy') : '—'}</TableCell>
                        <TableCell className="text-right font-mono text-sm">{fmt(lr.amountDue)}</TableCell>
                        <TableCell className="text-right font-mono text-sm text-emerald-600">{fmt(lr.amountRemitted)}</TableCell>
                        <TableCell>
                          <Badge className={lr.status === 'Remitted'
                            ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                            : 'bg-amber-100 text-amber-800 border-amber-200'
                          }>
                            {lr.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {lr.status === 'Pending' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs"
                              onClick={() => {
                                setRemitId(lr.id);
                                setRemitAmount(String(lr.amountDue));
                                setRemitDate(format(new Date(), 'yyyy-MM-dd'));
                                setRemitMode('NEFT');
                                setRemitUtr('');
                                setRemitNotes('');
                                setRemitOpen(true);
                              }}
                            >
                              Mark Remitted
                            </Button>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              {lr.remittanceDate ? format(new Date(lr.remittanceDate), 'dd MMM yyyy') : ''} {lr.utr ? `• ${lr.utr}` : ''}
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Mark all lender paid confirmation */}
      <AlertDialog open={markAllLenderPaidOpen} onOpenChange={setMarkAllLenderPaidOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark all lender invoices as paid?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  This will record lender payments for{" "}
                  <span className="font-medium text-foreground">{lenderUnpaidSummary.count}</span> unpaid
                  invoice(s) in{" "}
                  <span className="font-medium text-foreground">
                    {monthOptions.find((m) => m.value === selectedMonth)?.label ?? selectedMonth}
                  </span>
                  .
                </p>
                <p>
                  Total lender outstanding:{" "}
                  <span className="font-medium text-foreground">{fmt(lenderUnpaidSummary.amount)}</span>
                </p>
                <p>Payments will be recorded with payer <span className="font-medium text-foreground">payToLender</span>.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={markAllLenderPaidLoading}>Cancel</AlertDialogCancel>
            <Button onClick={handleMarkAllLenderPaid} disabled={markAllLenderPaidLoading}>
              {markAllLenderPaidLoading ? "Confirming…" : "Confirm"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mark Remitted Modal */}
      <Dialog open={remitOpen} onOpenChange={setRemitOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowUpRight className="h-5 w-5 text-primary" /> Mark Lender Remittance
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Remittance Date *</Label>
                <Input type="date" value={remitDate} onChange={(e) => setRemitDate(e.target.value)} className="h-9 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Payment Mode</Label>
                <Select value={remitMode} onValueChange={setRemitMode}>
                  <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NEFT">NEFT</SelectItem>
                    <SelectItem value="RTGS">RTGS</SelectItem>
                    <SelectItem value="IMPS">IMPS</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">UTR / Reference *</Label>
              <Input value={remitUtr} onChange={(e) => setRemitUtr(e.target.value)} placeholder="e.g. UTR123456789" className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Amount Remitted</Label>
              <Input type="number" value={remitAmount} onChange={(e) => setRemitAmount(e.target.value)} className="h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Input value={remitNotes} onChange={(e) => setRemitNotes(e.target.value)} placeholder="Optional" className="h-9 text-sm" />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setRemitOpen(false)}>Cancel</Button>
            <Button
              onClick={() => {
                if (!remitId) return;
                if (!remitUtr.trim()) {
                  toast({ title: 'UTR required', description: 'Please enter the bank reference.', variant: 'destructive' });
                  return;
                }
                markLenderRemitted(remitId, {
                  remittanceDate: remitDate,
                  paymentMode: remitMode,
                  utr: remitUtr.trim(),
                  notes: remitNotes || undefined,
                  amountRemitted: parseFloat(remitAmount) || undefined,
                });
                toast({ title: 'Remittance recorded', description: `Marked ${fmt(parseFloat(remitAmount) || 0)} as remitted` });
                setRemitOpen(false);
                setRemitId(null);
              }}
            >
              Confirm Remittance
            </Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Invoice Side Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-lg overflow-y-auto">
          {freshSelectedInvoice && selectedSplit && (
            <>
              <SheetHeader>
                <div className="flex items-center justify-between gap-2 pr-8">
                  <SheetTitle className="flex items-center gap-2">
                    {freshSelectedInvoice.rsInvoiceCode ?? freshSelectedInvoice.invoiceId}
                    <Badge className={`text-xs ${getInvoiceStatusColor(selectedSheetStatus)}`}>
                      {selectedSheetStatus}
                    </Badge>
                  </SheetTitle>
                  {currentTabInvoices.length > 1 && currentIndex >= 0 && (
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={currentIndex <= 0}
                        onClick={() => navigateInvoice(-1)}
                        aria-label="Previous invoice"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <span className="px-1 tabular-nums">{currentIndex + 1} / {currentTabInvoices.length}</span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7"
                        disabled={currentIndex >= currentTabInvoices.length - 1}
                        onClick={() => navigateInvoice(1)}
                        aria-label="Next invoice"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </SheetHeader>

              <div className="mt-4 space-y-4">
                {/* Invoice summary */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{freshSelectedInvoice.customer.name}</p></div>
                  <div><span className="text-muted-foreground">Schedule</span><p className="font-medium">{getScheduleDisplayIds(freshSelectedInvoice)}</p></div>
                  <div><span className="text-muted-foreground">Due Date</span><p className="font-medium">{format(new Date(freshSelectedInvoice.dueDate), "dd MMM yyyy")}</p></div>
                  <div><span className="text-muted-foreground">Astrafin Payable</span><p className="font-medium">{fmt(getAstrafinPayableAmount(freshSelectedInvoice))}</p></div>
                  <div><span className="text-muted-foreground">Lender Payable</span><p className="font-medium">{fmt(getLenderPayableAmount(freshSelectedInvoice))}</p></div>
                  <div><span className="text-muted-foreground">Instalment</span><p className="font-medium">{getInstalmentNo(freshSelectedInvoice)}</p></div>
                  <div><span className="text-muted-foreground">Grand Total</span><p className="font-medium">{fmt(freshSelectedInvoice.grandTotalRounded)}</p></div>
                </div>

                <Separator />

                {/* Payment Breakdown — tab-specific */}
                <div>
                  <h4 className="text-sm font-semibold mb-2">Payment Breakdown</h4>
                  <div className="rounded-lg border overflow-hidden text-sm">
                    <div className="flex justify-between px-3 py-2 bg-muted/30">
                      <span className="text-muted-foreground">Base Rental</span>
                      <span className="font-medium">{fmt(freshSelectedInvoice.baseRounded ?? 0)}</span>
                    </div>
                    <div className="flex justify-between px-3 py-2">
                      <span className="text-muted-foreground">GST</span>
                      <span className="font-medium">{fmt(freshSelectedInvoice.gstRaw ?? 0)}</span>
                    </div>
                    <div className="flex justify-between px-3 py-2 bg-muted/30">
                      <span className="text-muted-foreground">TDS (deducted)</span>
                      <span className="font-medium text-destructive">-{fmt(freshSelectedInvoice.tdsRounded ?? 0)}</span>
                    </div>
                    <Separator />
                    <div className="flex justify-between px-3 py-2.5 bg-muted/20 font-semibold">
                      <span>Total Amount</span>
                      <span>{fmt(freshSelectedInvoice.grandTotalRounded)}</span>
                    </div>
                    <Separator />
                    {activeTab === "astrafin" ? (
                      <div className="flex justify-between px-3 py-2.5 bg-emerald-50">
                        <span className="font-semibold text-emerald-700">Pay to Astrafin</span>
                        <span className="font-bold text-emerald-700">{fmt(getAstrafinPayableAmount(freshSelectedInvoice))}</span>
                      </div>
                    ) : (
                      <div className="flex justify-between px-3 py-2.5 bg-primary/5">
                        <span className="font-semibold text-primary">Pay to Lender</span>
                        <span className="font-bold text-primary">{fmt(getLenderPayableAmount(freshSelectedInvoice))}</span>
                      </div>
                    )}
                  </div>
                </div>

                {freshSelectedInvoice.autoGeneratedPdfLink && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full gap-2 text-xs"
                    onClick={() => {
                      const url = toAbsoluteFileUrl(freshSelectedInvoice.autoGeneratedPdfLink);
                      if (url) window.open(url, "_blank", "noopener,noreferrer");
                    }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" /> View Invoice Details
                  </Button>
                )}

                {/* Payment history — amounts and refs for active tab payer only */}
                {selectedSheetPayments.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold mb-2">Payment History</h4>
                    <div className="space-y-2">
                      {selectedSheetPayments.map((p, index) => {
                        const payment = p as PaymentRecord & { recordedBy?: string; recordedAt?: string };
                        const isExcel = payment.paidSource === "ASTRAFIN_EXCEL";
                        return (
                          <div
                            key={p.id ?? `${p.date}-${index}`}
                            className="flex items-start justify-between gap-3 text-sm bg-muted/50 rounded-lg px-3 py-2"
                          >
                            <div className="min-w-0 space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{fmt(p.amount)}</span>
                                {p.paymentMode && (
                                  <span className="text-muted-foreground text-xs">{p.paymentMode}</span>
                                )}
                                {isExcel ? (
                                  <Badge variant="outline" className="h-5 px-1.5 gap-1 bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium">
                                    <FileSpreadsheet className="h-3 w-3" /> Reconciled via Excel
                                  </Badge>
                                ) : payment.paidSource === "manual" ? (
                                  <Badge variant="outline" className="h-5 px-1.5 gap-1 bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-medium">
                                    <User className="h-3 w-3" /> Manual entry
                                  </Badge>
                                ) : null}
                              </div>
                              {p.reference && (
                                <p className="text-[11px] text-muted-foreground truncate">Ref: {p.reference}</p>
                              )}
                              {(payment.recordedBy || payment.recordedAt) && (
                                <p className="text-[11px] text-muted-foreground">
                                  Updated by <span className="font-medium text-foreground">{payment.recordedBy || "System"}</span>
                                  {payment.recordedAt && ` • ${format(new Date(payment.recordedAt), "dd MMM yyyy, hh:mm a")}`}
                                </p>
                              )}
                            </div>
                            <span className="text-muted-foreground text-xs whitespace-nowrap">{format(new Date(p.date), "dd MMM yyyy")}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Payment Lock Warning */}
                {isInvoiceLocked(freshSelectedInvoice) && (
                  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg px-3 py-2 text-sm">
                    <CheckCircle2 className="h-4 w-4 shrink-0" />
                    <span>This invoice is fully paid and locked for further payments.</span>
                  </div>
                )}

                {/* Mark Paid form */}
                {!isInvoiceLocked(freshSelectedInvoice) && (
                  <div className="space-y-3">
                    <h4 className="text-sm font-semibold flex items-center gap-1.5">
                      <DollarSign className="h-3.5 w-3.5" /> Record Payment
                    </h4>
                    <div className="space-y-2">
                      <Label>Amount (₹)</Label>
                      <Input disabled={true} type="number" value={payAmount} onChange={e => setPayAmount(e.target.value)} placeholder="Enter amount" />
                    </div>
                    <div className="space-y-2">
                      <Label>Payment Date</Label>
                      <Input type="date" value={payDate} onChange={e => setPayDate(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Reference / UTR</Label>
                      <Input value={payRef} onChange={e => setPayRef(e.target.value)} placeholder="e.g. HDFCR520260302..." />
                    </div>
                    <Button
                      className="w-full mt-2"
                      onClick={handleMarkPaid}
                      disabled={isRecordingPayment}
                    >
                      {isRecordingPayment ? (
                        <>
                          <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Recording…
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4 mr-2" /> Record Payment
                        </>
                      )}
                    </Button>
                    <div className="flex items-start gap-1.5 rounded-md bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                      <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>To match this invoice from a bank statement, use <span className="font-medium text-foreground">Upload Statement</span> at the top of the page — reconciliation runs across all invoices at once.</span>
                    </div>
                  </div>
                )}

              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Bulk Reconciliation Dialog */}
      <Dialog open={bulkOpen} onOpenChange={handleBulkOpenChange}>
        <DialogContent
          className="max-w-5xl max-h-[85vh] overflow-y-auto"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
        >
          <div className="relative">
          {(bulkProcessing || bulkApplying) && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-background/85 backdrop-blur-[1px]">
              <InlineLoader
                label={
                  bulkProcessing
                    ? "Parsing statement and matching invoices…"
                    : "Applying payments…"
                }
              />
            </div>
          )}
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-600 text-white shadow-sm">
                <FileSpreadsheet className="h-4 w-4" />
              </span>
              Bank Statement Reconciliation
            </DialogTitle>
            <p className="text-xs text-muted-foreground pl-10 -mt-1">
              Auto-match deposits to open invoices using UTR, amount and counterparty name.
            </p>
          </DialogHeader>

          {bulkTransactions.length === 0 ? (
            <div className="space-y-5 py-4">
              {/* Drag & drop card — Excel themed */}
              <div
                className={`relative overflow-hidden rounded-2xl border-2 border-dashed border-emerald-300 bg-gradient-to-br from-emerald-50/70 via-background to-background p-8 text-center transition-all hover:border-emerald-500 hover:shadow-md group ${bulkProcessing ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
                onClick={() => !bulkProcessing && document.getElementById("bulk-file-input")?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-emerald-600','bg-emerald-50'); }}
                onDragLeave={(e) => { e.currentTarget.classList.remove('border-emerald-600','bg-emerald-50'); }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('border-emerald-600','bg-emerald-50');
                  const f = e.dataTransfer.files?.[0];
                  if (f) handleBulkFile(f);
                }}
              >
                {/* Decorative Excel-style tile */}
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white shadow-lg shadow-emerald-500/30 group-hover:scale-105 transition-transform">
                  <FileSpreadsheet className="h-8 w-8" strokeWidth={2} />
                  <span className="absolute mt-9 ml-9 inline-flex h-5 w-5 items-center justify-center rounded-full bg-background border border-emerald-200 shadow-sm">
                    <FileUp className="h-3 w-3 text-emerald-600" />
                  </span>
                </div>
                <p className="text-base font-semibold">Drop your bank statement here</p>
                <p className="text-sm text-muted-foreground mt-1">
                  or <span className="text-emerald-700 font-medium underline-offset-2 group-hover:underline">browse files</span> from your computer
                </p>
                <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-muted-foreground">
                  <Badge variant="outline" className="bg-background gap-1 font-mono">
                    <FileSpreadsheet className="h-3 w-3 text-emerald-600" /> .xlsx
                  </Badge>
                  <Badge variant="outline" className="bg-background gap-1 font-mono">
                    <FileSpreadsheet className="h-3 w-3 text-emerald-600" /> .xls
                  </Badge>
                  <span>•</span>
                  <span>Max 10 MB</span>
                </div>
                <input id="bulk-file-input" type="file" accept=".xlsx,.xls" className="hidden"
                  onChange={e => { const f = e.target.files?.[0]; if (f) handleBulkFile(f); }} />
              </div>

              {/* Feature strip */}
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
                  <Sparkles className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Auto-match</p>
                    <p className="text-muted-foreground">UTR, amount & name matching</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
                  <ShieldCheck className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Duplicate guard</p>
                    <p className="text-muted-foreground">UTRs cannot be applied twice</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 rounded-lg border bg-muted/30 p-3">
                  <CheckCircle2 className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">FIFO allocation</p>
                    <p className="text-muted-foreground">Oldest dues cleared first</p>
                  </div>
                </div>
              </div>

              <p className="text-[11px] text-muted-foreground text-center">
                Currently supports <span className="font-medium text-foreground">ICICI Bank</span> statement format.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {(() => {
                const unmatched = bulkTransactions.filter(t => !t.matchedInvoiceIds || t.matchedInvoiceIds.length === 0);
                const unmatchedAmt = unmatched.reduce((s, t) => s + t.depositAmount, 0);
                return (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-wrap">
                      <p className="text-sm text-muted-foreground">
                        {bulkFileName} — <span className="font-medium text-foreground">{bulkTransactions.length} credit transactions</span>
                      </p>
                      {unmatched.length > 0 && (
                        <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 text-xs gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          Suspense: {unmatched.length} txn • {fmt(unmatchedAmt)}
                        </Badge>
                      )}
                      <div className="flex items-center gap-2">
                        <Switch checked={showMatchesOnly} onCheckedChange={setShowMatchesOnly} id="show-matches" />
                        <Label htmlFor="show-matches" className="text-xs text-muted-foreground cursor-pointer">Show matches only</Label>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={resetBulkDialogState} disabled={bulkApplying || bulkProcessing}>
                        Upload New
                      </Button>
                      <Button size="sm" onClick={acceptBulkHighConfidence} className="gap-1" disabled={bulkApplying || bulkProcessing}>
                        <Check className="h-3.5 w-3.5" /> Accept All ≥90%
                      </Button>
                    </div>
                  </div>
                );
              })()}


              {filteredBulkTxns.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <FileSpreadsheet className="h-10 w-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No matching transactions found</p>
                  <p className="text-xs mt-1">Try toggling "Show matches only" to see all transactions</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredBulkTxns.map((txn:any) => {
                    const matchedInvs = txn.matchedInvoiceIds?.map((id:any) => invoices.find((i:any) => i.invoiceId === id)).filter(Boolean) as Invoice[];
                    const hasFlags = txn.flags && (txn.flags as any[]).length > 0;
                    return (
                      <div key={txn.id+Math.random().toString(36).substring(2, 15)} className={`border rounded-lg overflow-hidden ${hasFlags ? 'border-amber-300' : ''}`}>
                        <div className="grid grid-cols-2 divide-x">
                          <div className="p-4 space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold">{fmt(txn.depositAmount)}</span>
                              <Badge variant="outline" className="text-xs">{txn.paymentMode || "—"}</Badge>
                            </div>
                            <p className="text-xs text-muted-foreground line-clamp-2">{txn.narration}</p>
                            {txn.utrNumber && <p className="text-xs text-muted-foreground">UTR: {txn.utrNumber}</p>}
                            {txn.counterpartyName && <p className="text-xs font-medium">From: {txn.counterpartyName}</p>}
                            <p className="text-xs text-muted-foreground">{txn.valueDate ? format(new Date(txn.valueDate), "dd MMM yyyy") : "—"}</p>
                            {/* Flags */}
                            {hasFlags && (
                              <div className="space-y-1 mt-2">
                                {txn.flags!.map((flag:any, idx:any) => {
                                  const cfg = FLAG_CONFIG[flag as TransactionFlag];
                                  const FlagIcon = cfg.icon;
                                  return (
                                    <div key={idx+Math.random().toString(36).substring(2, 15)} className={`flex items-start gap-1.5 rounded px-2 py-1 text-xs ${cfg.color}`}>
                                      <FlagIcon className="h-3 w-3 mt-0.5 shrink-0" />
                                      <span>{txn.flagMessages?.[idx] || cfg.label}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                          <div className="p-4">
                            {matchedInvs && matchedInvs.length > 0 ? (
                              <div className="space-y-2">
                                {matchedInvs.map((inv: ReconciliationInvoice) => (
                                    <div key={inv.invoiceId ?? inv.rsInvoiceCode} className="text-sm">
                                      <p className="font-medium">{inv.rsInvoiceCode}</p>
                                      <p className="text-muted-foreground text-xs">
                                        {inv.customer.name} • Astrafin: {fmt(getAstrafinPayableAmount(inv))}
                                      </p>
                                    </div>
                                  ))}
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <Badge className="bg-primary/10 text-primary text-xs">{txn.matchConfidence}% match</Badge>
                                  {matchedInvs.length > 1 && (
                                    <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">FIFO oldest-first</Badge>
                                  )}
                                  {txn.tranchePart && (
                                    <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">
                                      Tranche {txn.tranchePart.index}/{txn.tranchePart.total} · linked ₹{txn.tranchePart.invoiceAmount.toLocaleString('en-IN')}
                                    </Badge>
                                  )}
                                  {txn.unappliedAmount && txn.unappliedAmount > 0 && (
                                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                                      Unapplied {fmt(txn.unappliedAmount)} → customer advance
                                    </Badge>
                                  )}
                                </div>
                                {txn.tranchePart && (() => {
                                  const siblings = bulkTransactions.filter((t:any) => txn.tranchePart!.siblingTxnIds.includes(t.id));
                                  return (
                                    <div className="mt-2 rounded-md border border-violet-200 bg-violet-50/40 p-2 space-y-1">
                                      <p className="text-[11px] font-medium text-violet-800">Linked tranches:</p>
                                      {siblings.map((s:any, i:any) => (
                                        <div key={s.id+Math.random().toString(36).substring(2, 15)} className="flex items-center justify-between text-[11px] text-violet-900">
                                          <span>#{i + 1} · {s.valueDate ? format(new Date(s.valueDate), 'dd MMM') : '—'} {s.utrNumber ? `· ${s.utrNumber}` : ''}</span>
                                          <span className="font-semibold">{fmt(s.depositAmount)}</span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                                <div className="flex gap-2 mt-2">
                                  <Button size="sm" className="gap-1 flex-1" disabled={bulkApplying || bulkProcessing} onClick={() => {
                                    if (txn.tranchePart) {
                                      acceptTrancheGroup(txn);
                                    } else if (txn.matchedInvoiceIds && txn.matchedInvoiceIds.length > 0) {
                                      acceptMatch(txn as any, txn.matchedInvoiceIds[0]);
                                    }
                                  }}>
                                    <Check className="h-3 w-3" /> Accept{txn.tranchePart ? ` (${txn.tranchePart.total} tranches)` : matchedInvs.length > 1 ? ` (${matchedInvs.length} invoices)` : ''}
                                  </Button>
                                  <Button size="sm" variant="ghost" className="gap-1 flex-1" disabled={bulkApplying || bulkProcessing} onClick={() => setBulkTransactions(prev => prev.filter(t => t.id !== txn.id))}>
                                    <X className="h-3 w-3" /> Ignore
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <p className="text-sm text-muted-foreground">No match found</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end border-t pt-4 mt-2">
            <Button
              variant="outline"
              onClick={closeBulkDialog}
              disabled={bulkApplying || bulkProcessing}
            >
              Cancel
            </Button>
          </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Sub-components
function InvoiceTable({ invoices, statusSide, onRowClick, getScheduleDisplayIds, getInstalmentNo, getPaidAmount, statusIcon, amountLabel, getDisplayAmount, showLender }: {
  invoices: ReconciliationInvoice[];
  statusSide: "astrafin" | "lender";
  onRowClick: (inv: ReconciliationInvoice) => void;
  getScheduleDisplayIds: (inv: ReconciliationInvoice) => string;
  getInstalmentNo: (inv: Invoice) => string;
  getPaidAmount: (inv: ReconciliationInvoice) => number;
  statusIcon: (status: string) => React.ReactNode;
  amountLabel: string;
  getDisplayAmount: (inv: ReconciliationInvoice) => number;
  showLender: boolean;
}) {
  if (invoices.length === 0) {
    return (
      <Card className="border shadow-sm">
        <CardContent className="py-16 text-center">
          <FileSpreadsheet className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No invoices found for this period</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border shadow-sm overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/30">
            <TableHead className="text-xs font-semibold">Invoice</TableHead>
            <TableHead className="text-xs font-semibold">Customer</TableHead>
            {showLender && <TableHead className="text-xs font-semibold">Lender</TableHead>}
            <TableHead className="text-xs font-semibold">Schedule</TableHead>
            {/* <TableHead className="text-xs font-semibold">Case</TableHead> */}
            <TableHead className="text-xs font-semibold text-center">Inst #</TableHead>
            <TableHead className="text-xs font-semibold">Due Date</TableHead>
            <TableHead className="text-xs font-semibold text-right">{amountLabel}</TableHead>
            <TableHead className="text-xs font-semibold text-right">Paid</TableHead>
            <TableHead className="text-xs font-semibold text-right">Balance</TableHead>
            <TableHead className="text-xs font-semibold text-center">Status</TableHead>
            <TableHead className="text-xs font-semibold text-center w-10"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {invoices.map((inv) => {
            const displayAmt = getDisplayAmount(inv);
            const paid = getPaidAmount(inv);
            const status = getSideInvoiceStatus(inv, statusSide, displayAmt, paid);
            const balance = Math.max(0, displayAmt - paid);
            const accent =
              status === "Paid" ? "border-l-emerald-500"
              : status === "Overdue" ? "border-l-destructive"
              : "border-l-primary";
            return (
              <TableRow
                key={inv.invoiceId ?? inv.rsInvoiceCode}
                className={`cursor-pointer hover:bg-muted/40 transition-colors border-l-4 ${accent}`}
                onClick={() => onRowClick(inv)}
              >
                <TableCell className="text-xs font-medium">
                  <div className="flex items-center gap-1.5">
                    <span>{inv.rsInvoiceCode}</span>
                    {hasExcelReconciliation(inv, statusSide) && (
                      <span title="Reconciled via bank statement (Excel)" className="inline-flex h-4 w-4 items-center justify-center rounded bg-emerald-100 text-emerald-700">
                        <FileSpreadsheet className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-xs">{inv.customer.name}</TableCell>
                {showLender && (
                  <TableCell className="text-xs">
                    {inv.lenderName || inv.lenderAvatar ? (
                      <div className="flex items-center gap-2 min-w-0">
                        {inv.lenderAvatar ? (
                          <Avatar className="rounded-md shrink-0" style={{ height: "28px", width: "44px" }}>
                            <AvatarImage
                              src={toAbsoluteLenderAvatarUrl(inv.lenderAvatar)}
                              alt={inv.lenderName || "Lender"}
                              className="object-contain"
                            />
                          </Avatar>
                        ) : null}
                        <span className="truncate">{inv.lenderName || "—"}</span>
                      </div>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                <TableCell className="text-xs">{getScheduleDisplayIds(inv)}</TableCell>
                <TableCell className="text-xs text-center">{getInstalmentNo(inv)}</TableCell>
                <TableCell className="text-xs">{format(new Date(inv.dueDate), "dd MMM yyyy")}</TableCell>
                <TableCell className="text-xs text-right font-medium">{fmt(displayAmt)}</TableCell>
                <TableCell className="text-xs text-right text-emerald-600">{fmt(paid)}</TableCell>
                <TableCell className="text-xs text-right text-destructive">{fmt(balance)}</TableCell>
                <TableCell className="text-center">
                  <Badge variant="outline" className={`text-[10px] gap-1 ${getInvoiceStatusColor(status)}`}>
                    {statusIcon(status)} {status}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <Eye className="h-4 w-4 text-muted-foreground mx-auto" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Card>
  );
}

export default PaymentReconciliation;
